import React, { useState } from 'react';
import {
  X,
  Copy,
  Check,
  Share2,
  MapPin,
  Calendar,
  Users,
  Compass,
  ExternalLink,
  Sparkles
} from 'lucide-react';

export default function SocialShareModal({ trip, isOpen, onClose }) {
  const [copied, setCopied] = useState(false);

  if (!isOpen || !trip) return null;

  // Generate deep link
  const originUrl = window.location.origin;
  const shareUrl = `${originUrl}/dashboard/trip/${trip.id}?inviteCode=${trip.inviteCode || ''}`;
  const destination = trip.destination || 'Dream Destination';
  const title = trip.title || `${destination} Expedition`;
  const totalDays = trip.totalDays || (trip.days ? trip.days.length : 3);
  const budgetStr = trip.budget ? `₹${Number(trip.budget).toLocaleString()}` : 'Budget Friendly';
  const organizerName = trip.user?.name || 'Explorer';
  const coverImage =
    trip.coverImageUrl ||
    trip.summary?.imageUrl ||
    'https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?q=80&w=1200&auto=format&fit=crop';

  const shareText = `🌍 Join my travel expedition to ${destination} on Sync & Explore!\n📅 ${totalDays} Days | 💰 Budget: ${budgetStr}\n✈️ Click the link to review, accept your traveler slot, or propose edits:`;

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: title,
          text: shareText,
          url: shareUrl,
        });
      } catch (err) {
        console.warn('Native share dismissed or failed:', err);
      }
    } else {
      handleCopyLink();
    }
  };

  // Social sharing links
  const socialPlatforms = [
    {
      name: 'WhatsApp',
      color: 'bg-[#25D366] hover:bg-[#20bd5a] text-white',
      icon: '💬',
      url: `https://api.whatsapp.com/send?text=${encodeURIComponent(`${shareText}\n${shareUrl}`)}`,
    },
    {
      name: 'Telegram',
      color: 'bg-[#0088cc] hover:bg-[#0077b5] text-white',
      icon: '✈️',
      url: `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`,
    },
    {
      name: 'Twitter / X',
      color: 'bg-black hover:bg-neutral-800 text-white',
      icon: '𝕏',
      url: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`,
    },
    {
      name: 'LinkedIn',
      color: 'bg-[#0A66C2] hover:bg-[#084e96] text-white',
      icon: '💼',
      url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
    },
    {
      name: 'Facebook',
      color: 'bg-[#1877F2] hover:bg-[#155ec2] text-white',
      icon: '👥',
      url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
    },
  ];

  return (
    <div className="fixed inset-0 z-50 bg-black/65 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-200">
      <div className="bg-white rounded-[2rem] max-w-lg w-full shadow-2xl border border-gray-200 overflow-hidden flex flex-col max-h-[92vh] animate-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gray-50/70">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl bg-teal-50 text-[#2A9D8F] flex items-center justify-center">
              <Share2 className="w-4 h-4" />
            </div>
            <div>
              <h3 className="text-sm font-black text-gray-900">Share Expedition</h3>
              <p className="text-[11px] text-gray-500">Invite companions to view, accept, or propose changes</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-xl transition-colors cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6 overflow-y-auto space-y-6">
          {/* ───────────────────────────────────────────────────────────── */}
          {/* INSTAGRAM POST STYLE TRAVEL CARD PREVIEW                      */}
          {/* ───────────────────────────────────────────────────────────── */}
          <div className="rounded-3xl overflow-hidden border border-gray-200 shadow-md bg-white">
            {/* Post Header */}
            <div className="px-4 py-3 flex items-center justify-between bg-white border-b border-gray-100">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-full p-[2px] bg-gradient-to-tr from-[#2A9D8F] via-[#E76F51] to-[#F4A261]">
                  <div className="w-full h-full rounded-full bg-white flex items-center justify-center text-[10px] font-bold text-gray-800 uppercase">
                    {organizerName.slice(0, 2)}
                  </div>
                </div>
                <div>
                  <p className="text-xs font-bold text-gray-900 leading-none">{organizerName}</p>
                  <p className="text-[10px] text-gray-400 mt-0.5">Sync &amp; Explore Organizer</p>
                </div>
              </div>
              {trip.inviteCode && (
                <span className="font-mono text-[10px] font-bold bg-teal-50 text-[#2A9D8F] px-2 py-0.5 rounded-lg border border-teal-100">
                  {trip.inviteCode}
                </span>
              )}
            </div>

            {/* Post Image Banner */}
            <div className="relative h-48 sm:h-56 bg-slate-100 overflow-hidden">
              <img
                src={coverImage}
                alt={destination}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.src = 'https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?q=80&w=1200&auto=format&fit=crop';
                }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>

              {/* Badges on Image */}
              <div className="absolute top-3 right-3 px-3 py-1 bg-black/60 backdrop-blur-md rounded-full text-white text-[11px] font-bold">
                {totalDays} Days
              </div>

              {/* Bottom Card Overlay */}
              <div className="absolute bottom-3 left-3 right-3 text-white space-y-1">
                <div className="flex items-center gap-1.5 text-teal-300 text-xs font-bold">
                  <MapPin className="w-3.5 h-3.5" />
                  <span>{destination}</span>
                </div>
                <h4 className="text-lg font-black text-white leading-tight drop-shadow-md">
                  {title}
                </h4>
                <div className="flex items-center gap-3 text-[11px] text-gray-200 pt-0.5">
                  <span>Budget: <strong className="text-emerald-300 font-bold">{budgetStr}</strong></span>
                  <span>•</span>
                  <span>Slots: <strong>{trip.travelers || 2} Travelers</strong></span>
                </div>
              </div>
            </div>

            {/* Post Footer Call-to-Action */}
            <div className="px-4 py-3 bg-gray-50 border-t border-gray-100 flex items-center justify-between text-xs">
              <span className="text-gray-500 font-medium">Accept Slot or Propose Changes (PR)</span>
              <span className="text-[#2A9D8F] font-bold flex items-center gap-1">
                <span>View &amp; Join</span>
                <ExternalLink className="w-3 h-3" />
              </span>
            </div>
          </div>

          {/* ───────────────────────────────────────────────────────────── */}
          {/* DEEP LINK COPY FIELD                                          */}
          {/* ───────────────────────────────────────────────────────────── */}
          <div className="space-y-2">
            <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider">
              Expedition Deep Link
            </label>
            <div className="flex items-center gap-2 p-1.5 bg-gray-50 rounded-2xl border border-gray-200">
              <input
                type="text"
                readOnly
                value={shareUrl}
                className="flex-1 bg-transparent px-3 py-1.5 text-xs text-gray-700 font-mono outline-none truncate"
              />
              <button
                type="button"
                onClick={handleCopyLink}
                className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer shadow-xs ${
                  copied
                    ? 'bg-emerald-600 text-white'
                    : 'bg-[#2A9D8F] hover:bg-[#238177] text-white'
                }`}
              >
                {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                <span>{copied ? 'Copied!' : 'Copy Link'}</span>
              </button>
            </div>
          </div>

          {/* ───────────────────────────────────────────────────────────── */}
          {/* ONE-CLICK SOCIAL PLATFORM SHARE BUTTONS                       */}
          {/* ───────────────────────────────────────────────────────────── */}
          <div className="space-y-2.5">
            <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider">
              Share to Social Channels
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
              {socialPlatforms.map((platform) => (
                <a
                  key={platform.name}
                  href={platform.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`flex items-center justify-center gap-2 py-2.5 px-3 rounded-xl text-xs font-bold shadow-xs transition-transform hover:scale-[1.02] cursor-pointer ${platform.color}`}
                >
                  <span>{platform.icon}</span>
                  <span>{platform.name}</span>
                </a>
              ))}

              {typeof navigator !== 'undefined' && navigator.share && (
                <button
                  type="button"
                  onClick={handleNativeShare}
                  className="flex items-center justify-center gap-2 py-2.5 px-3 rounded-xl text-xs font-bold bg-gray-800 hover:bg-gray-900 text-white shadow-xs transition-transform hover:scale-[1.02] cursor-pointer"
                >
                  <Share2 className="w-3.5 h-3.5" />
                  <span>More Options</span>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
          <p className="text-[11px] text-gray-500">
            Companions clicking this link can accept as-is or propose edits like a GitHub PR.
          </p>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-white hover:bg-gray-100 border border-gray-200 text-gray-700 rounded-xl text-xs font-bold transition-colors cursor-pointer"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
